import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import { ErrorTimeout } from "./error";
import { Event } from "./event";
import { UrlUtils } from "./urlUtils";
import { OidcSettings, IFrameWindowParams, IWindow, NavigateParams, NavigateResponse } from "@/shared/model/auth";

export const messageSource = "oidc-client";

export interface MessageData {
    source: string;
    url: string;
    keepOpen: boolean;
}

export abstract class AbstractChildWindow implements IWindow {
    protected readonly _abort = new Event<[reason: Error]>("Window navigation aborted");
    protected readonly _disposeHandlers = new Set<() => void>();

    protected _window: WindowProxy | null = null;

    public async navigate(params: NavigateParams): Promise<NavigateResponse> {
        if (!this._window) {
            throw new Error("Attempted to navigate on a disposed window");
        }

        if (isDevelopmentEnv()) console.debug("setting URL in window");
        this._window.location.replace(params.url);

        const { url, keepOpen } = await new Promise<MessageData>((resolve, reject) => {
            const listener = (e: MessageEvent) => {
                const data: MessageData | undefined = e.data;
                const origin = params.scriptOrigin ?? window.location.origin;
                if (e.origin !== origin || data?.source !== messageSource) {
                    // silently discard events not intended for us
                    return;
                }
                try {
                    const state = UrlUtils.readParams(data.url, params.response_mode).get("state");
                    if (!state) {
                        if (isDevelopmentEnv()) console.warn("no state found in response url");
                    }
                    if (e.source !== this._window && state !== params.state) {
                        // MessageEvent source is a relatively modern feature, we can't rely on it
                        // so we also inspect the payload for a matching state key as an alternative
                        return;
                    }
                } catch (err) {
                    this._dispose();
                    reject(new Error("Invalid response from window"));
                }
                resolve(data);
            };
            window.addEventListener("message", listener, false);
            this._disposeHandlers.add(() => window.removeEventListener("message", listener, false));
            this._disposeHandlers.add(
                this._abort.addHandler((reason) => {
                    this._dispose();
                    reject(reason);
                }),
            );
        });
        if (isDevelopmentEnv()) console.debug("got response from window");
        this._dispose();

        if (!keepOpen) {
            this.close();
        }

        return { url };
    }

    public abstract close(): void;

    private _dispose(): void {
        for (const dispose of this._disposeHandlers) {
            dispose();
        }
        this._disposeHandlers.clear();
    }

    protected static _notifyParent(
        parent: Window,
        url: string,
        keepOpen = false,
        targetOrigin = window.location.origin,
    ): void {
        parent.postMessage(
            {
                source: messageSource,
                url,
                keepOpen,
            } as MessageData,
            targetOrigin,
        );
    }
}

export class IFrameWindow extends AbstractChildWindow {
    private _frame: HTMLIFrameElement | null;
    private readonly _timeoutInSeconds: number;

    public constructor({ requestTimeoutInSeconds = 60 }: IFrameWindowParams) {
        super();
        this._timeoutInSeconds = requestTimeoutInSeconds;

        this._frame = IFrameWindow.createHiddenIframe();
        this._window = this._frame.contentWindow;
    }

    private static createHiddenIframe(): HTMLIFrameElement {
        const iframe = document.createElement("iframe");

        // shotgun approach
        iframe.style.visibility = "hidden";
        iframe.style.position = "fixed";
        iframe.style.left = "-1000px";
        iframe.style.top = "0";
        iframe.width = "0";
        iframe.height = "0";

        document.body.appendChild(iframe);
        return iframe;
    }

    public async navigate(params: NavigateParams): Promise<NavigateResponse> {
        if (isDevelopmentEnv()) console.debug("navigate: Using timeout of:", this._timeoutInSeconds);
        const timer = setTimeout(
            () => void this._abort.raise(new ErrorTimeout("IFrame timed out without a response")),
            this._timeoutInSeconds * 1000,
        );
        this._disposeHandlers.add(() => clearTimeout(timer));

        return await super.navigate(params);
    }

    public close(): void {
        if (this._frame) {
            if (this._frame.parentNode) {
                this._frame.addEventListener(
                    "load",
                    (ev) => {
                        const frame = ev.target as HTMLIFrameElement;
                        frame.parentNode?.removeChild(frame);
                        void this._abort.raise(new Error("IFrame removed from DOM"));
                    },
                    true,
                );
                this._frame.contentWindow?.location.replace("about:blank");
            }
            this._frame = null;
        }
        this._window = null;
    }

    public static notifyParent(url: string, targetOrigin?: string): void {
        return super._notifyParent(window.parent, url, false, targetOrigin);
    }
}

export interface INavigator {
    prepare(params: unknown): Promise<IWindow>;

    callback(url: string, params?: unknown): Promise<void>;
}

export class IFrameNavigator implements INavigator {
    constructor(private _settings: OidcSettings) {}

    public async prepare({
        requestTimeoutInSeconds = this._settings.requestTimeoutInSeconds,
    }: IFrameWindowParams): Promise<IFrameWindow> {
        return new IFrameWindow({ requestTimeoutInSeconds });
    }

    public async callback(url: string): Promise<void> {
        IFrameWindow.notifyParent(url, this._settings.iframeNotifyParentOrigin);
    }
}
